/**
 * HourWeekdayHeatmap: 7 weekdays x 24 hours, intensity = value (e.g. orders). Quantitative single-hue ramp (4 steps),
 * with a Less/More key; exact numbers are in the tooltip and the hidden table.
 * Props: caption, matrix (7 rows, Monday first, each 24 values for hours 0-23), formatValue, loading?, className?.
 * Keyboard: one tab stop; arrow keys move between cells, Home/End jump to the first/last hour, Escape closes the tooltip.
 */
import { KeyboardEvent, PointerEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartState, DataTable, Tooltip } from './chartKit';
import { GRID, MUTED, clamp, colorOf, useWidth } from './chartUtils';

export interface HourWeekdayHeatmapProps {
    caption: string;
    matrix: number[][];
    formatValue: (n: number) => string;
    loading?: boolean;
    className?: string;
}

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const LEVEL_OPACITY = [0.25, 0.5, 0.75, 1] as const;
const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;

export const HourWeekdayHeatmap = ({ caption, matrix, formatValue, loading, className }: HourWeekdayHeatmapProps) => {
    const { t } = useTranslation('charts');
    const { ref, width } = useWidth();
    const svgRef = useRef<SVGSVGElement>(null);
    const [active, setActive] = useState<[number, number] | null>(null);

    const at = (d: number, h: number) => matrix[d]?.[h] ?? 0;
    const max = DAYS.reduce((m, _, d) => Math.max(m, ...Array.from({ length: 24 }, (_x, h) => at(d, h))), 0);
    const labelW = 40;
    const cell = Math.max((width - labelW) / 24, 4);
    const cellH = clamp(cell, 18, 30);
    const headH = 18;
    const height = headH + 7 * cellH;
    const every = Math.ceil(30 / cell);
    const levelOf = (v: number) => (v <= 0 || max <= 0 ? 0 : Math.min(4, Math.ceil((v / max) * 4)));

    const cellAt = (e: PointerEvent): [number, number] => {
        const rect = svgRef.current?.getBoundingClientRect();
        const h = clamp(Math.floor((e.clientX - (rect?.left ?? 0) - labelW) / cell), 0, 23);
        const d = clamp(Math.floor((e.clientY - (rect?.top ?? 0) - headH) / cellH), 0, 6);
        return [d, h];
    };
    const onKey = (e: KeyboardEvent) => {
        const [d, h] = active ?? [0, 0];
        const next: Record<string, [number, number]> = {
            ArrowRight: [d, h + 1], ArrowLeft: [d, h - 1], ArrowDown: [d + 1, h], ArrowUp: [d - 1, h], Home: [d, 0], End: [d, 23],
        };
        const target = next[e.key];
        if (target) {
            e.preventDefault();
            setActive([clamp(target[0], 0, 6), clamp(target[1], 0, 23)]);
        } else if (e.key === 'Escape') setActive(null);
    };

    const dayName = (d: number, form: 'weekdayShort' | 'weekdayLong') => t(`${form}.${DAYS[d] ?? 'mon'}`);
    const rows = DAYS.map((_, d) => [dayName(d, 'weekdayLong'), ...Array.from({ length: 24 }, (_x, h) => formatValue(at(d, h)))]);
    const empty = matrix.length === 0 || max <= 0;
    const sel = active ? { d: active[0], h: active[1], v: at(active[0], active[1]) } : null;
    const summary = sel ? `${dayName(sel.d, 'weekdayLong')} ${hh(sel.h)}: ${formatValue(sel.v)}` : '';

    return (
        <div className={className}>
            <ChartState loading={loading} empty={empty} height={160}>
                <div ref={ref} className="relative">
                    <div
                        tabIndex={0}
                        role="group"
                        aria-label={`${caption}. ${t('keyboardHint')}`}
                        className="relative rounded-md"
                        style={{ touchAction: 'pan-y' }}
                        onKeyDown={onKey}
                        onFocus={() => setActive((a) => a ?? [0, 0])}
                        onBlur={() => setActive(null)}
                        onPointerDown={(e) => setActive(cellAt(e))}
                        onPointerMove={(e) => setActive(cellAt(e))}
                        onPointerLeave={(e) => e.pointerType === 'mouse' && document.activeElement !== e.currentTarget && setActive(null)}
                    >
                        <svg ref={svgRef} width="100%" height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" focusable="false" className="block">
                            {Array.from({ length: 24 }, (_, h) =>
                                h % every === 0 ? (
                                    <text key={h} x={labelW + (h + 0.5) * cell} y={11} textAnchor="middle" fontSize="11" fill={MUTED}>
                                        {String(h).padStart(2, '0')}
                                    </text>
                                ) : null,
                            )}
                            {DAYS.map((_, d) => (
                                <g key={d}>
                                    <text x={labelW - 6} y={headH + (d + 0.5) * cellH} textAnchor="end" dominantBaseline="central" fontSize="11" fill={MUTED}>
                                        {dayName(d, 'weekdayShort')}
                                    </text>
                                    {Array.from({ length: 24 }, (_x, h) => {
                                        const lv = levelOf(at(d, h));
                                        const on = sel?.d === d && sel.h === h;
                                        return (
                                            <rect
                                                key={h}
                                                x={labelW + h * cell + 0.5}
                                                y={headH + d * cellH + 0.5}
                                                width={Math.max(cell - 1, 1)}
                                                height={cellH - 1}
                                                rx="2"
                                                fill={lv === 0 ? GRID : colorOf(0)}
                                                fillOpacity={lv === 0 ? 1 : LEVEL_OPACITY[lv - 1]}
                                                stroke={on ? 'var(--text-strong, #1c1c1c)' : undefined}
                                                strokeWidth={on ? 2 : undefined}
                                            />
                                        );
                                    })}
                                </g>
                            ))}
                        </svg>
                        {sel && (
                            <Tooltip
                                x={labelW + (sel.h + 0.5) * cell}
                                y={headH + sel.d * cellH + cellH}
                                width={width}
                                title={`${dayName(sel.d, 'weekdayLong')} ${hh(sel.h)}`}
                                rows={[{ key: 'v', value: formatValue(sel.v) }]}
                            />
                        )}
                    </div>
                    <div aria-hidden="true" className="mt-2 flex items-center justify-end gap-1 text-small text-muted">
                        <span>{t('heatmapLow')}</span>
                        <svg width="76" height="12" focusable="false">
                            <rect width="16" height="12" rx="2" fill={GRID} />
                            {LEVEL_OPACITY.map((o, i) => (
                                <rect key={o} x={(i + 1) * 15 + 1} width="14" height="12" rx="2" fill={colorOf(0)} fillOpacity={o} />
                            ))}
                        </svg>
                        <span>{t('heatmapHigh')}</span>
                    </div>
                    <span className="sr-only" aria-live="polite">{summary}</span>
                </div>
            </ChartState>
            <DataTable caption={caption} headers={[t('day'), ...Array.from({ length: 24 }, (_, h) => hh(h))]} rows={empty ? [] : rows} />
        </div>
    );
};
